import { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  useTheme,
  useMediaQuery,
  Fade,
  Zoom,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  EmojiEvents as TrophyIcon,
  LocalBar as BeerIcon,
  Sports as GameIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';

const TeamCumulativeStandingsView = ({ tournament, tournamentId }) => {
  const [teams, setTeams] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTeam, setExpandedTeam] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchTeamStandings();
  }, [tournamentId]);

  const fetchTeamStandings = async () => {
    try {
      // Fetch teams
      const teamsRef = collection(db, 'tournaments', tournamentId, 'teams');
      const teamsSnapshot = await getDocs(teamsRef);
      const teamsData = teamsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        members: doc.data().members || [],
      }));

      // Fetch players to get updated map scores
      const playersRef = collection(db, 'tournaments', tournamentId, 'players');
      const playersSnapshot = await getDocs(playersRef);
      const playersData = playersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        mapScores: doc.data().mapScores || {},
        drinkCount: doc.data().drinkCount || 0,
      }));
      setPlayers(playersData);

      // Calculate team totals and sort by points
      const teamsWithTotals = teamsData.map((team) => {
        let totalDrinks = 0;

        const enrichedMembers = team.members.map((member) => {
          const playerData = playersData.find((p) => p.userId === member.userId);
          if (playerData) {
            const playerTotal = Object.values(playerData.mapScores || {}).reduce(
              (sum, score) => sum + (score || 0),
              0
            );
            totalDrinks += playerData.drinkCount || 0;
            return {
              ...member,
              mapScores: playerData.mapScores || {},
              drinkCount: playerData.drinkCount || 0,
              totalScore: playerTotal,
              imageUrl: playerData.imageUrl || playerData.profilePicture || null,
            };
          }
          return member;
        });

        // Calculate team points using only top 2 players per map/round
        let totalTeamPoints = 0;
        const mapNames = tournament.maps || [];

        mapNames.forEach((mapName) => {
          // Get all team members' scores for this map
          const mapScores = enrichedMembers
            .map((member) => member.mapScores?.[mapName] || 0)
            .sort((a, b) => b - a) // Sort descending
            .slice(0, 2); // Take only top 2 players

          // Add the top 2 scores for this map to team total
          totalTeamPoints += mapScores.reduce((sum, score) => sum + score, 0);
        });

        return {
          ...team,
          members: enrichedMembers,
          totalPoints: totalTeamPoints,
          totalDrinks,
          averageScore: enrichedMembers.length > 0 ? totalTeamPoints / enrichedMembers.length : 0,
        };
      });

      // Sort teams by total points (descending)
      teamsWithTotals.sort((a, b) => b.totalPoints - a.totalPoints);
      setTeams(teamsWithTotals);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching team standings:', error);
      setLoading(false);
    }
  };

  const handleTeamExpand = (teamId) => {
    setExpandedTeam(expandedTeam === teamId ? null : teamId);
  };

  // Creative Beer Counter Component
  const BeerCounter = ({ count, maxCount = 10 }) => {
    const beerPercentage = Math.min((count / maxCount) * 100, 100);
    const beerColor =
      count === 0 ? '#e0e0e0' : count <= 3 ? '#FFA726' : count <= 6 ? '#FF7043' : '#D32F2F';

    return (
      <Tooltip title={`${count} drink${count !== 1 ? 's' : ''}`} arrow>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              position: 'relative',
              width: 24,
              height: 32,
              background: 'linear-gradient(180deg, #D4AF37 0%, #F4E4BC 10%, transparent 10%)',
              borderRadius: '0 0 8px 8px',
              border: '2px solid #8D6E63',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'flex-end',
            }}
          >
            {/* Beer foam */}
            {count > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: Math.max(2, 30 - beerPercentage * 0.28),
                  left: 2,
                  right: 2,
                  height: 4,
                  background: '#F5F5F5',
                  borderRadius: '50%',
                  opacity: 0.9,
                  animation: count > 0 ? 'foam-bubble 2s ease-in-out infinite' : 'none',
                  '@keyframes foam-bubble': {
                    '0%, 100%': { transform: 'scale(1)', opacity: 0.9 },
                    '50%': { transform: 'scale(1.1)', opacity: 0.7 },
                  },
                }}
              />
            )}

            {/* Beer liquid */}
            <Box
              sx={{
                width: '100%',
                height: `${beerPercentage}%`,
                background: `linear-gradient(180deg, ${beerColor} 0%, ${beerColor}CC 100%)`,
                transition: 'height 0.8s ease-in-out, background 0.3s ease',
                borderRadius: '0 0 6px 6px',
                position: 'relative',
                '&::before':
                  count > 0
                    ? {
                        content: '""',
                        position: 'absolute',
                        top: '-2px',
                        left: '10%',
                        right: '10%',
                        height: '2px',
                        background: 'rgba(255,255,255,0.3)',
                        borderRadius: '50%',
                        animation: 'beer-shine 3s ease-in-out infinite',
                        '@keyframes beer-shine': {
                          '0%, 100%': { opacity: 0.3, transform: 'scale(1)' },
                          '50%': { opacity: 0.6, transform: 'scale(1.2)' },
                        },
                      }
                    : {},
              }}
            />

            {/* Glass handle */}
            <Box
              sx={{
                position: 'absolute',
                right: -8,
                top: '30%',
                width: 6,
                height: '40%',
                border: '2px solid #8D6E63',
                borderLeft: 'none',
                borderRadius: '0 4px 4px 0',
              }}
            />
          </Box>

          {count > 0 && (
            <Typography
              variant="caption"
              sx={{ color: beerColor, fontWeight: 'bold', minWidth: 20 }}
            >
              {count}
            </Typography>
          )}
        </Box>
      </Tooltip>
    );
  };

  // Position badge component
  const PositionBadge = ({ position }) => {
    const colors = {
      1: { bg: 'linear-gradient(135deg, #FFD700, #FFA000)', icon: '#B8860B' },
      2: { bg: 'linear-gradient(135deg, #C0C0C0, #9E9E9E)', icon: '#757575' },
      3: { bg: 'linear-gradient(135deg, #CD7F32, #8D6E63)', icon: '#5D4037' },
    };

    const style = colors[position] || {
      bg: 'linear-gradient(135deg, #64B5F6, #42A5F5)',
      icon: '#1976D2',
    };

    return (
      <Zoom in={true} style={{ transitionDelay: `${position * 100}ms` }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: style.bg,
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.1rem',
            boxShadow: position <= 3 ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.2)',
            border: position === 1 ? '3px solid #FFF' : 'none',
            position: 'relative',
            '&::after':
              position === 1
                ? {
                    content: '""',
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    right: -2,
                    bottom: -2,
                    borderRadius: '50%',
                    background: 'linear-gradient(45deg, #FFD700, #FFA000)',
                    zIndex: -1,
                    animation: 'winner-glow 2s ease-in-out infinite',
                    '@keyframes winner-glow': {
                      '0%, 100%': { opacity: 0.7, transform: 'scale(1)' },
                      '50%': { opacity: 1, transform: 'scale(1.1)' },
                    },
                  }
                : {},
          }}
        >
          {position <= 3 && position === 1 && <TrophyIcon sx={{ color: style.icon }} />}
          {position > 3 || position !== 1 ? position : ''}
        </Box>
      </Zoom>
    );
  };

  const getMapColumns = () => {
    if (!tournament.maps || tournament.maps.length === 0) return [];
    return tournament.maps;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LinearProgress sx={{ width: '50%' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            color: 'white',
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
            mb: 2,
          }}
        >
          🏆 Team Standings 🏆
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {teams.map((team, index) => (
          <Grid item xs={12} key={team.id}>
            <Fade in={true} style={{ transitionDelay: `${index * 150}ms` }}>
              <Card
                sx={{
                  background:
                    index === 0
                      ? 'linear-gradient(135deg, rgba(255,215,0,0.1), rgba(255,193,7,0.05))'
                      : index === 1
                        ? 'linear-gradient(135deg, rgba(192,192,192,0.1), rgba(158,158,158,0.05))'
                        : index === 2
                          ? 'linear-gradient(135deg, rgba(205,127,50,0.1), rgba(141,110,99,0.05))'
                          : 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border:
                    index === 0
                      ? '2px solid rgba(255,215,0,0.3)'
                      : '1px solid rgba(255,255,255,0.1)',
                  boxShadow:
                    index === 0 ? '0 8px 32px rgba(255,215,0,0.2)' : '0 4px 20px rgba(0,0,0,0.3)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow:
                      index === 0
                        ? '0 12px 40px rgba(255,215,0,0.3)'
                        : '0 8px 30px rgba(0,0,0,0.4)',
                  },
                }}
              >
                <Accordion
                  expanded={expandedTeam === team.id}
                  onChange={() => handleTeamExpand(team.id)}
                  sx={{
                    background: 'transparent',
                    boxShadow: 'none',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 2 }}>
                      <PositionBadge position={index + 1} />

                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          background: team.color || '#000',
                          border: '2px solid white',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        }}
                      />

                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                          {team.name}
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            mt: 1,
                            flexWrap: 'wrap',
                          }}
                        >
                          <Chip
                            label={`${team.totalPoints.toFixed(1)} pts`}
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BeerCounter count={team.totalDrinks} />
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                              Team Total
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'white', mb: 3 }}>
                        Team Members & Map Scores
                      </Typography>

                      <TableContainer
                        component={Paper}
                        sx={{ background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(5px)' }}
                      >
                        <Table size={isMobile ? 'small' : 'medium'}>
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                                Player
                              </TableCell>
                              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                                Drinks
                              </TableCell>
                              {getMapColumns().map((mapName) => (
                                <TableCell
                                  key={mapName}
                                  align="center"
                                  sx={{ color: 'white', fontWeight: 'bold' }}
                                >
                                  {mapName}
                                </TableCell>
                              ))}
                              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                                Total
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {team.members
                              .sort((a, b) => (b.totalScore || 0) - (a.totalScore || 0))
                              .map((member) => (
                                <TableRow
                                  key={member.userId}
                                  sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}
                                >
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar
                                        src={member.imageUrl}
                                        sx={{
                                          width: 32,
                                          height: 32,
                                          border: '2px solid rgba(255,255,255,0.3)',
                                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                                        }}
                                      >
                                        {!member.imageUrl && member.username[0]?.toUpperCase()}
                                      </Avatar>
                                      <Box>
                                        <Typography
                                          variant="body2"
                                          sx={{ color: 'white', fontWeight: 'bold' }}
                                        >
                                          {member.username}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                      }}
                                    >
                                      <BeerCounter count={member.drinkCount || 0} />
                                    </Box>
                                  </TableCell>
                                  {getMapColumns().map((mapName) => (
                                    <TableCell key={mapName} align="center">
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          color: 'white',
                                          fontWeight:
                                            (member.mapScores?.[mapName] || 0) > 0
                                              ? 'bold'
                                              : 'normal',
                                          opacity: (member.mapScores?.[mapName] || 0) > 0 ? 1 : 0.5,
                                        }}
                                      >
                                        {(member.mapScores?.[mapName] || 0).toFixed(1)}
                                      </Typography>
                                    </TableCell>
                                  ))}
                                  <TableCell align="center">
                                    <Chip
                                      label={(member.totalScore || 0).toFixed(1)}
                                      color="primary"
                                      size="small"
                                      sx={{ fontWeight: 'bold' }}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </Card>
            </Fade>
          </Grid>
        ))}
      </Grid>

      {teams.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', background: 'rgba(255,255,255,0.05)' }}>
          <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
            No team standings available yet
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Teams and scores will appear here once the tournament begins!
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default TeamCumulativeStandingsView;
